/**
 * Detects members whose setup will stop them working, before anyone notices
 * downstream.
 *
 * Every one of these states looks fine on the member list today: the person
 * exists, has roles, and appears in the table. The failure only shows up when
 * scheduling cannot find them, or they cannot log into the app -- by which point
 * it reads as a bug rather than as a setup mistake made weeks earlier.
 */

import type { OrgMemberResponse } from './types';

export type MemberHealthIssue =
  | 'not-assignable-anywhere'
  | 'no-disciplines'
  | 'missing-app-account';

export interface MemberHealthContext {
  /**
   * Whether the Roger App account list actually loaded. It is fetched
   * separately and its failure is non-fatal, so an empty list means either
   * "nobody has an account" or "we could not ask" -- and reporting the second as
   * the first would flag every clinician at once.
   */
  appAccountsLoaded: boolean;
  /** Whether this member has a linked Roger App account. */
  /** Whether they hold an *active* Roger App account. */
  hasAppAccount: boolean;
}

/**
 * Titles that license no visit discipline, mirroring
 * `_INTENTIONALLY_NO_DISCIPLINE` in `app/services/clinical_titles.py`. A
 * chaplain holds a title but is not expected to take visits.
 */
const TITLES_WITHOUT_DISCIPLINE: readonly string[] = ['CHAPLAIN', 'OTHER'];

/**
 * A member is only expected to see patients if they hold a clinical title.
 * Back-office staff legitimately have no clinics, no disciplines and no app
 * account, and flagging them would bury the real problems in noise.
 *
 * A title that licenses no discipline is the same case: a chaplain with no
 * clinic and no app account is correctly configured, and warning about it
 * forever is exactly the noise this badge exists to cut through. Unless they
 * *are* assignable somewhere -- then somebody has set them up to take visits
 * and the gaps matter again.
 */
function isClinical(
  member: OrgMemberResponse,
  assignableSomewhere: boolean
): boolean {
  if (member.user_type == null) return false;
  if (TITLES_WITHOUT_DISCIPLINE.includes(member.user_type)) {
    return assignableSomewhere;
  }
  return true;
}

export function getMemberHealthIssues(
  member: OrgMemberResponse,
  context: MemberHealthContext
): MemberHealthIssue[] {
  // Someone who has not accepted their invite has not been set up yet. Their
  // configuration is incomplete by definition, not incorrectly.
  if (member.is_pending) return [];

  const issues: MemberHealthIssue[] = [];
  const assignments = member.clinic_assignments ?? [];
  const assignableAt = assignments.filter(a => a.is_assignable_clinician);

  // Assignable as a clinician somewhere, but with no discipline there, means
  // scheduling can see them and can never offer them a visit.
  if (assignableAt.some(a => (a.disciplines ?? []).length === 0)) {
    issues.push('no-disciplines');
  }

  if (isClinical(member, assignableAt.length > 0)) {
    if (assignableAt.length === 0) {
      issues.push('not-assignable-anywhere');
    }
    if (context.appAccountsLoaded && !context.hasAppAccount) {
      issues.push('missing-app-account');
    }
  }

  return issues;
}

export const MEMBER_HEALTH_LABELS: Record<MemberHealthIssue, string> = {
  'not-assignable-anywhere': 'Not assignable at any clinic',
  'no-disciplines': 'No visit types at a clinic',
  'missing-app-account': 'No Roger App account',
};

export const MEMBER_HEALTH_EXPLANATIONS: Record<MemberHealthIssue, string> = {
  'not-assignable-anywhere':
    'This person has a clinical title but cannot be scheduled anywhere. Add them as a clinician at the clinics they work at.',
  'no-disciplines':
    'This person is a clinician at a clinic but has no visit types there, so they will never be offered a visit. Set the visit types they can take.',
  'missing-app-account':
    'This person has a clinical title but no Roger App account, so they cannot document visits on a phone.',
};
